import { useEffect, useState } from 'preact/hooks';
import {
  getStoreProducts, getStoreCredit, createStoreOrder, getMyStoreOrders, cancelStoreOrder,
  CACHE_KEYS, ApiError,
  type StoreProduct, type StoreCredit, type StoreOrder,
} from '../lib/api';
import { getCached } from '../lib/cache';
import {
  getCart, cartCount, cartTotal, addToCart, setQty, removeFromCart, clearCart, cartLines,
  subscribeCart,
} from '../lib/cart';
import { Skeleton } from '../components/Skeleton';
import { haptic } from '../lib/telegram';

function fmtNum(n: number): string {
  return Math.round(n).toLocaleString('es-DO');
}

const MONTH_ES_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function fmtShortDate(iso: string): string {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso || '';
  return `${parseInt(m[3], 10)} ${MONTH_ES_SHORT[parseInt(m[2], 10) - 1] || ''}`;
}

function estadoBadgeClass(estado: string): string {
  if (estado === 'Despachado') return 'completado';
  if (estado === 'Pendiente') return 'pendiente';
  return '';
}

export function Tienda({ onBack }: { onBack: () => void }) {
  const [view, setView] = useState<'catalog' | 'orders'>('catalog');

  // Catalog state
  const [products, setProducts] = useState<StoreProduct[] | null>(null);
  const [credit, setCredit] = useState<StoreCredit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals / flow
  const [selected, setSelected] = useState<StoreProduct | null>(null);
  const [selectedTalla, setSelectedTalla] = useState('');
  const [cartOpen, setCartOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [receipt, setReceipt] = useState<StoreOrder | null>(null);

  // Orders ("Mis pedidos")
  const [orders, setOrders] = useState<StoreOrder[] | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  // Re-render on any cart mutation.
  const [, setCartTick] = useState(0);
  useEffect(() => subscribeCart(() => setCartTick((t) => t + 1)), []);

  useEffect(() => { loadCatalog(); }, []);

  async function loadCatalog() {
    setError(null);
    const cached = getCached<{ products: StoreProduct[] }>(CACHE_KEYS.store);
    if (cached) { setProducts(cached.value.products); setLoading(false); }
    else setLoading(true);
    try {
      const [prod, cr] = await Promise.all([getStoreProducts(), getStoreCredit().catch(() => null)]);
      setProducts(prod.products);
      if (cr) setCredit(cr);
    } catch (e) {
      if (!cached) setError((e as Error).message || 'No pudimos cargar la tienda');
    } finally {
      setLoading(false);
    }
  }

  async function loadOrders() {
    setOrdersLoading(true);
    try {
      const r = await getMyStoreOrders();
      setOrders(r.orders);
    } catch (e) {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }

  function openOrders() {
    setView('orders');
    if (orders === null) loadOrders();
  }

  function openDetail(p: StoreProduct) {
    setSelected(p);
    setSelectedTalla(p.tallas.length > 0 ? p.tallas[0] : '');
    haptic('light');
  }

  function addSelectedToCart() {
    if (!selected) return;
    addToCart({
      sku: selected.sku,
      nombre: selected.nombre,
      talla: selected.tallas.length > 0 ? selectedTalla : '',
      precioUnit: selected.precio,
      fotoUrl: selected.fotoUrl,
    });
    haptic('success');
    setSelected(null);
  }

  async function confirmPurchase() {
    const lines = cartLines();
    if (lines.length === 0 || placing) return;
    const total = cartTotal();
    setPlacing(true);
    haptic('medium');
    try {
      const r = await createStoreOrder(lines);
      haptic('success');
      clearCart();
      setCartOpen(false);
      setReceipt(r.order);
      if (typeof r.available === 'number') {
        setCredit((c) => (c ? { ...c, available: r.available as number } : c));
      }
      // Refresh credit + catalog stock in the background.
      getStoreCredit().then(setCredit).catch(() => {});
      getStoreProducts().then((p) => setProducts(p.products)).catch(() => {});
      if (orders !== null) loadOrders();
    } catch (e) {
      haptic('error');
      const err = e as ApiError;
      const msg = (err.body && typeof err.body.message === 'string' && err.body.message) || '';
      if (err.message === 'insufficient_balance') {
        alert(msg || `No tienes saldo suficiente para esta compra (RD$ ${fmtNum(total)}).`);
      } else if (err.message === 'out_of_stock') {
        alert(msg || 'Uno de los productos se quedó sin stock. Revisa tu carrito.');
        getStoreProducts().then((p) => setProducts(p.products)).catch(() => {});
      } else {
        alert(msg || 'No pudimos procesar la compra. Intenta de nuevo.');
      }
    } finally {
      setPlacing(false);
    }
  }

  async function handleCancelOrder(idCompra: string) {
    if (cancelingId) return;
    if (!confirm('¿Cancelar este pedido? Se te devuelve el saldo descontado.')) return;
    setCancelingId(idCompra);
    haptic('medium');
    try {
      await cancelStoreOrder(idCompra);
      haptic('success');
      await loadOrders();
    } catch (e) {
      haptic('error');
      const err = e as ApiError;
      const msg = (err.body && typeof err.body.message === 'string' && err.body.message) || '';
      alert(msg || 'No pudimos cancelar el pedido.');
    } finally {
      setCancelingId(null);
    }
  }

  const count = cartCount();
  const total = cartTotal();
  const available = credit ? credit.available : null;
  const overLimit = available !== null && total > available;

  return (
    <div class="screen">
      <header class="app-header tienda-header">
        <button class="tienda-back" onClick={() => { haptic('light'); onBack(); }} aria-label="Volver">‹</button>
        <h1>Tienda</h1>
        <button class="tienda-cart-btn" onClick={() => setCartOpen(true)} aria-label="Carrito">
          🛒{count > 0 && <span class="tienda-cart-badge">{count}</span>}
        </button>
      </header>

      <div class="tienda-tabs">
        <button class={`tienda-tab ${view === 'catalog' ? 'active' : ''}`} onClick={() => setView('catalog')}>Catálogo</button>
        <button class={`tienda-tab ${view === 'orders' ? 'active' : ''}`} onClick={openOrders}>Mis pedidos</button>
      </div>

      {view === 'catalog' && (
        <CatalogView
          loading={loading} error={error} products={products} credit={credit}
          onRetry={loadCatalog} onSelect={openDetail}
        />
      )}

      {view === 'orders' && (
        <OrdersView
          loading={ordersLoading} orders={orders} cancelingId={cancelingId}
          onCancel={handleCancelOrder} onShop={() => setView('catalog')}
        />
      )}

      {/* Product detail */}
      {selected && (
        <div class="modal-bg show" onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div class="modal">
            <div class="modal-handle" />
            <div class="modal-body">
              {selected.fotoUrl && <img class="tienda-detail-photo" src={selected.fotoUrl} alt={selected.nombre} />}
              <h2>{selected.nombre}</h2>
              <div class="tienda-price">RD$ {fmtNum(selected.precio)}</div>
              {selected.descripcion && <p class="tienda-desc">{selected.descripcion}</p>}
              <div class="lt-sub" style={{ marginBottom: 8 }}>
                {selected.stock > 0 ? `${selected.stock} disponible${selected.stock === 1 ? '' : 's'}` : 'Agotado'}
                {selected.lugarRecogida && ` · Recoge en ${selected.lugarRecogida}`}
              </div>

              {selected.tallas.length > 0 && (
                <>
                  <div class="section-header" style={{ margin: '8px 0 6px' }}>Talla</div>
                  <div class="tienda-size-row">
                    {selected.tallas.map((t) => (
                      <button
                        key={t}
                        class={`tienda-size ${selectedTalla === t ? 'active' : ''}`}
                        onClick={() => setSelectedTalla(t)}
                      >{t}</button>
                    ))}
                  </div>
                </>
              )}

              <div class="modal-actions">
                <button class="btn primary" disabled={selected.stock <= 0} onClick={addSelectedToCart}>
                  {selected.stock <= 0 ? 'Agotado' : 'Agregar al carrito'}
                </button>
                <button class="btn ghost" onClick={() => setSelected(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart */}
      {cartOpen && (
        <div class="modal-bg show" onClick={(e) => { if (e.target === e.currentTarget) setCartOpen(false); }}>
          <div class="modal">
            <div class="modal-handle" />
            <div class="modal-body">
              <h2>Tu carrito</h2>
              {getCart().length === 0 ? (
                <div class="empty"><div class="ic">🛒</div><p>Tu carrito está vacío.</p></div>
              ) : (
                <>
                  {getCart().map((it) => (
                    <div class="tienda-cart-row" key={`${it.sku}|${it.talla}`}>
                      <div class="tienda-cart-info">
                        <div class="lt-title">{it.nombre}{it.talla ? ` · ${it.talla}` : ''}</div>
                        <div class="lt-sub">RD$ {fmtNum(it.precioUnit)} c/u</div>
                      </div>
                      <div class="tienda-qty">
                        <button onClick={() => setQty(it.sku, it.talla, it.cantidad - 1)} aria-label="Quitar uno">−</button>
                        <span>{it.cantidad}</span>
                        <button onClick={() => setQty(it.sku, it.talla, it.cantidad + 1)} aria-label="Agregar uno">+</button>
                      </div>
                      <button class="tienda-cart-del" onClick={() => removeFromCart(it.sku, it.talla)} aria-label="Eliminar">✕</button>
                    </div>
                  ))}

                  <div class="tienda-cart-total">
                    <span>Total</span>
                    <strong>RD$ {fmtNum(total)}</strong>
                  </div>
                  {available !== null && (
                    <div class={`saldo-pill ${overLimit ? 'warning' : ''}`} style={{ marginTop: 8 }}>
                      {overLimit
                        ? `Te faltan RD$ ${fmtNum(total - available)} de saldo disponible`
                        : `Disponible para gastar: RD$ ${fmtNum(available)}`}
                    </div>
                  )}
                  <p class="tienda-desc" style={{ marginTop: 10 }}>
                    Se descontará <strong>RD$ {fmtNum(total)}</strong> de tu próximo pago.
                  </p>

                  <div class="modal-actions">
                    <button class="btn primary" disabled={placing || overLimit} onClick={confirmPurchase}>
                      {placing ? 'Procesando...' : 'Confirmar compra'}
                    </button>
                    <button class="btn ghost" onClick={() => setCartOpen(false)}>Seguir comprando</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Receipt */}
      {receipt && (
        <div class="modal-bg show" onClick={(e) => { if (e.target === e.currentTarget) setReceipt(null); }}>
          <div class="modal">
            <div class="modal-handle" />
            <div class="modal-body">
              <div style={{ textAlign: 'center', fontSize: '2.2rem' }}>✅</div>
              <h2 style={{ textAlign: 'center' }}>¡Pedido confirmado!</h2>
              <div class="lt-sub" style={{ textAlign: 'center', marginBottom: 12 }}>{receipt.idCompra}</div>
              <div class="card">
                {receipt.items.map((it) => (
                  <div class="ledger-row" key={`${it.sku}|${it.talla}`}>
                    <div class="ledger-info">
                      <div class="lt-title">{it.cantidad}× {it.nombre}{it.talla ? ` · ${it.talla}` : ''}</div>
                    </div>
                    <div class="ledger-amt">RD$ {fmtNum(it.precioTotal)}</div>
                  </div>
                ))}
                <div class="ledger-row">
                  <div class="ledger-info"><div class="lt-title">Total descontado de tu pago</div></div>
                  <div class="ledger-amt amt-neg">−RD$ {fmtNum(receipt.total)}</div>
                </div>
              </div>
              {receipt.lugarRecogida && (
                <p class="tienda-desc" style={{ marginTop: 10 }}>
                  Recoge tu pedido en <strong>{receipt.lugarRecogida}</strong>. Te avisaremos cuando esté listo.
                </p>
              )}
              <div class="modal-actions">
                <button class="btn ghost" onClick={() => { setReceipt(null); openOrders(); }}>Ver mis pedidos</button>
                <button class="btn primary" onClick={() => setReceipt(null)}>Listo</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CatalogView({ loading, error, products, credit, onRetry, onSelect }: {
  loading: boolean; error: string | null; products: StoreProduct[] | null; credit: StoreCredit | null;
  onRetry: () => void; onSelect: (p: StoreProduct) => void;
}) {
  if (loading && !products) {
    return (
      <div style={{ padding: 'var(--space-3)' }}>
        <div class="tienda-grid">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={170} borderRadius={12} />)}
        </div>
      </div>
    );
  }
  if (error && !products) {
    return (
      <div class="error-screen">
        <h2>No pudimos cargar</h2>
        <p>{error}</p>
        <button class="btn primary" onClick={onRetry}>Reintentar</button>
      </div>
    );
  }
  const list = products || [];
  if (list.length === 0) {
    return <div class="empty"><div class="ic">👕</div><p>Aún no hay uniformes disponibles.</p></div>;
  }
  return (
    <div style={{ padding: 'var(--space-3)' }}>
      {credit && (
        <div class="saldo-pill" style={{ marginBottom: 'var(--space-3)' }}>
          Disponible para gastar: RD$ {fmtNum(credit.available)}
        </div>
      )}
      <div class="tienda-grid">
        {list.map((p) => (
          <button class="prod-card" key={p.sku} onClick={() => onSelect(p)}>
            {p.fotoUrl
              ? <img class="prod-photo" src={p.fotoUrl} alt={p.nombre} loading="lazy" />
              : <div class="prod-photo prod-photo-empty">👕</div>}
            <div class="prod-name">{p.nombre}</div>
            <div class="prod-price">RD$ {fmtNum(p.precio)}</div>
            {p.stock <= 3 && <div class="prod-stock">{p.stock === 0 ? 'Agotado' : `Quedan ${p.stock}`}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

function OrdersView({ loading, orders, cancelingId, onCancel, onShop }: {
  loading: boolean; orders: StoreOrder[] | null; cancelingId: string | null;
  onCancel: (id: string) => void; onShop: () => void;
}) {
  if (loading && !orders) {
    return (
      <div style={{ padding: 'var(--space-3)' }}>
        {[0, 1].map((i) => <Skeleton key={i} height={90} borderRadius={12} style={{ marginBottom: 10 }} />)}
      </div>
    );
  }
  const list = orders || [];
  if (list.length === 0) {
    return (
      <div class="empty">
        <div class="ic">📦</div>
        <p>Aún no tienes pedidos.</p>
        <button class="btn primary" style={{ maxWidth: 220, marginTop: 12 }} onClick={onShop}>Ver catálogo</button>
      </div>
    );
  }
  return (
    <div style={{ padding: 'var(--space-3)' }}>
      {list.map((o) => (
        <div class="card" key={o.idCompra} style={{ marginBottom: 10 }}>
          <div class="ledger-row" style={{ alignItems: 'center' }}>
            <div class="ledger-info">
              <div class="lt-title">{o.idCompra}</div>
              <div class="lt-sub">{fmtShortDate(o.fecha)} · RD$ {fmtNum(o.total)}</div>
            </div>
            <span class={`badge ${estadoBadgeClass(o.estado)}`}>{o.estado}</span>
          </div>
          <div class="lt-sub" style={{ padding: '0 2px 6px' }}>
            {o.items.map((it) => `${it.cantidad}× ${it.nombre}${it.talla ? ` (${it.talla})` : ''}`).join(', ')}
          </div>
          {o.estado === 'Pendiente' && (
            <button
              class="btn danger-soft"
              disabled={cancelingId === o.idCompra}
              onClick={() => onCancel(o.idCompra)}
            >
              {cancelingId === o.idCompra ? 'Cancelando...' : 'Cancelar pedido'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
